// ============================================================================
// FILE ATTACHMENTS CONTEXT - GESTIÓN CENTRALIZADA DE ARCHIVOS ADJUNTOS
// ============================================================================

import React, { createContext, useContext, useReducer, useCallback } from 'react';
import apiClient from '../services/apiClient';

// ============================================================================
// CONFIGURACIÓN
// ============================================================================

// La subida usa XMLHttpRequest directamente (para reportar progreso, que el
// apiClient/fetch no expone), así que aún necesita la URL base aquí.
const API_BASE_URL = window.__APP_CONFIG__?.API_URL || 'http://localhost/task/backend/routes/api.php';

// ============================================================================
// CONTEXT CREATION
// ============================================================================

const FileAttachmentsContext = createContext();

// ============================================================================
// ESTADO INICIAL
// ============================================================================

const initialState = {
  // Archivos por tarea: { [taskId]: [attachment, ...] }
  taskAttachments: {},
  // Estados de upload: { [uploadId]: { status, progress, error } }
  uploadStates: {},
  // Loading states
  isLoading: {},
  // Errores
  errors: {}
};

// ============================================================================
// ACTIONS
// ============================================================================

const actionTypes = {
  SET_LOADING: 'SET_LOADING',
  SET_ATTACHMENTS: 'SET_ATTACHMENTS',
  ADD_ATTACHMENT: 'ADD_ATTACHMENT',
  REMOVE_ATTACHMENT: 'REMOVE_ATTACHMENT',
  SET_UPLOAD_STATE: 'SET_UPLOAD_STATE',
  REMOVE_UPLOAD_STATE: 'REMOVE_UPLOAD_STATE',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR'
};

// ============================================================================
// REDUCER
// ============================================================================

function fileAttachmentsReducer(state, action) {
  switch (action.type) {
    case actionTypes.SET_LOADING:
      return {
        ...state,
        isLoading: {
          ...state.isLoading,
          [action.taskId]: action.loading
        }
      };

    case actionTypes.SET_ATTACHMENTS:
      return {
        ...state,
        taskAttachments: {
          ...state.taskAttachments,
          [action.taskId]: action.attachments
        }
      };

    case actionTypes.ADD_ATTACHMENT:
      return {
        ...state,
        taskAttachments: {
          ...state.taskAttachments,
          [action.taskId]: [
            ...(state.taskAttachments[action.taskId] || []),
            action.attachment
          ]
        }
      };

    case actionTypes.REMOVE_ATTACHMENT:
      return {
        ...state,
        taskAttachments: {
          ...state.taskAttachments,
          [action.taskId]: (state.taskAttachments[action.taskId] || [])
            .filter(att => att.id !== action.attachmentId)
        }
      };

    case actionTypes.SET_UPLOAD_STATE:
      return {
        ...state,
        uploadStates: {
          ...state.uploadStates,
          [action.uploadId]: {
            status: action.status,
            progress: action.progress,
            error: action.error,
            file: action.file
          }
        }
      };

    case actionTypes.REMOVE_UPLOAD_STATE:
      const newUploadStates = { ...state.uploadStates };
      delete newUploadStates[action.uploadId];
      return {
        ...state,
        uploadStates: newUploadStates
      };

    case actionTypes.SET_ERROR:
      return {
        ...state,
        errors: {
          ...state.errors,
          [action.key]: action.error
        }
      };

    case actionTypes.CLEAR_ERROR:
      const newErrors = { ...state.errors };
      delete newErrors[action.key];
      return {
        ...state,
        errors: newErrors
      };

    default:
      return state;
  }
}

// ============================================================================
// PROVIDER COMPONENT
// ============================================================================

export function FileAttachmentsProvider({ children }) {
  const [state, dispatch] = useReducer(fileAttachmentsReducer, initialState);

  // ========================================================================
  // API FUNCTIONS
  // ========================================================================

  // Obtener archivos adjuntos de una tarea
  const fetchTaskAttachments = useCallback(async (taskId) => {
    if (!taskId) return;

    dispatch({ type: actionTypes.SET_LOADING, taskId, loading: true });
    dispatch({ type: actionTypes.CLEAR_ERROR, key: `fetch-${taskId}` });

    try {
      const result = await apiClient.get('attachments', { task_id: taskId });

      if (result.status === 'success') {
        dispatch({
          type: actionTypes.SET_ATTACHMENTS,
          taskId,
          attachments: result.data || []
        });
        return { success: true, data: result.data };
      } else {
        throw new Error(result.message || 'Error al obtener archivos');
      }
    } catch (error) {
      console.error('Error fetching attachments:', error);
      dispatch({
        type: actionTypes.SET_ERROR,
        key: `fetch-${taskId}`,
        error: error.message
      });
      return { success: false, error: error.message };
    } finally {
      dispatch({ type: actionTypes.SET_LOADING, taskId, loading: false });
    }
  }, []);

  // Subir archivo
  const uploadFile = useCallback(async (taskId, file, onProgress) => {
    if (!taskId || !file) {
      throw new Error('Task ID y archivo son requeridos');
    }

    const uploadId = `upload-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    // Configurar estado inicial
    dispatch({
      type: actionTypes.SET_UPLOAD_STATE,
      uploadId,
      status: 'uploading',
      progress: 0,
      error: null,
      file: {
        name: file.name,
        size: file.size,
        type: file.type
      }
    });

    try {
      const formData = new FormData();
      formData.append('file', file);

      const xhr = new XMLHttpRequest();

      // Configurar progreso
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          dispatch({
            type: actionTypes.SET_UPLOAD_STATE,
            uploadId,
            status: 'uploading',
            progress,
            error: null,
            file: {
              name: file.name,
              size: file.size,
              type: file.type
            }
          });
          onProgress?.(progress);
        }
      });

      // Promesa para manejar la respuesta
      const uploadPromise = new Promise((resolve, reject) => {
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const result = JSON.parse(xhr.responseText);
              if (result.status === 'success') {
                resolve(result.data);
              } else {
                reject(new Error(result.message || 'Error en la respuesta del servidor'));
              }
            } catch (error) {
              reject(new Error('Error al parsear respuesta del servidor'));
            }
          } else {
            reject(new Error(`HTTP ${xhr.status}: ${xhr.statusText}`));
          }
        };

        xhr.onerror = () => {
          reject(new Error('Error de red durante la subida'));
        };

        xhr.ontimeout = () => {
          reject(new Error('Timeout durante la subida'));
        };
      });

      // Configurar y enviar
      xhr.timeout = 120000; // 2 minutos
      xhr.open('POST', `${API_BASE_URL}?endpoint=upload&task_id=${taskId}`);
      const uploadToken = localStorage.getItem('token');
      if (uploadToken) {
        xhr.setRequestHeader('Authorization', `Bearer ${uploadToken}`);
      }
      xhr.send(formData);

      // Esperar resultado
      const attachment = await uploadPromise;

      // Actualizar estado exitoso
      dispatch({
        type: actionTypes.SET_UPLOAD_STATE,
        uploadId,
        status: 'success',
        progress: 100,
        error: null,
        file: {
          name: file.name,
          size: file.size,
          type: file.type
        }
      });

      // Agregar al estado de archivos
      dispatch({
        type: actionTypes.ADD_ATTACHMENT,
        taskId,
        attachment
      });

      // Limpiar estado de upload después de un momento
      setTimeout(() => {
        dispatch({ type: actionTypes.REMOVE_UPLOAD_STATE, uploadId });
      }, 3000);

      return { success: true, data: attachment, uploadId };

    } catch (error) {
      console.error('Upload error:', error);
      
      // Actualizar estado de error
      dispatch({
        type: actionTypes.SET_UPLOAD_STATE,
        uploadId,
        status: 'error',
        progress: 0,
        error: error.message,
        file: {
          name: file.name,
          size: file.size,
          type: file.type
        }
      });

      return { success: false, error: error.message, uploadId };
    }
  }, []);

  // Eliminar archivo
  const deleteAttachment = useCallback(async (taskId, attachmentId) => {
    if (!taskId || !attachmentId) {
      throw new Error('Task ID y attachment ID son requeridos');
    }

    try {
      const result = await apiClient.del(`attachment&id=${attachmentId}`);

      if (result.status === 'success') {
        // Remover del estado
        dispatch({
          type: actionTypes.REMOVE_ATTACHMENT,
          taskId,
          attachmentId
        });
        return { success: true };
      } else {
        throw new Error(result.message || 'Error al eliminar archivo');
      }
    } catch (error) {
      console.error('Delete error:', error);
      dispatch({
        type: actionTypes.SET_ERROR,
        key: `delete-${attachmentId}`,
        error: error.message
      });
      return { success: false, error: error.message };
    }
  }, []);

  // ========================================================================
  // UTILITY FUNCTIONS
  // ========================================================================

  // Obtener archivos de una tarea
  const getTaskAttachments = useCallback((taskId) => {
    return state.taskAttachments[taskId] || [];
  }, [state.taskAttachments]);

  // Obtener solo imágenes de una tarea
  const getTaskImages = useCallback((taskId) => {
    const attachments = state.taskAttachments[taskId] || [];
    return attachments.filter(att => att.isImage);
  }, [state.taskAttachments]);

  // Obtener archivos que no son imágenes
  const getTaskFiles = useCallback((taskId) => {
    const attachments = state.taskAttachments[taskId] || [];
    return attachments.filter(att => !att.isImage);
  }, [state.taskAttachments]);

  // Obtener estados de upload activos
  const getActiveUploads = useCallback((taskId) => {
    return Object.entries(state.uploadStates)
      .filter(([_, uploadState]) => uploadState.status === 'uploading')
      .map(([uploadId, uploadState]) => ({ uploadId, ...uploadState }));
  }, [state.uploadStates]);
  // Verificar si es imagen por tipo MIME
  const isImageFile = useCallback((fileType) => {
    return fileType.startsWith('image/');
  }, []);

  // Formatear tamaño de archivo
  const formatFileSize = useCallback((bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }, []);

  // ========================================================================
  // CONTEXT VALUE
  // ========================================================================

  const contextValue = {
    // Estado
    taskAttachments: state.taskAttachments,
    uploadStates: state.uploadStates,
    isLoading: state.isLoading,
    errors: state.errors,

    // API functions
    fetchTaskAttachments,
    uploadFile,
    deleteAttachment,

    // Utility functions
    getTaskAttachments,
    getTaskImages,
    getTaskFiles,
    getActiveUploads,
    isImageFile,
    formatFileSize,

    // Actions directas
    clearError: (key) => dispatch({ type: actionTypes.CLEAR_ERROR, key })
  };

  return (
    <FileAttachmentsContext.Provider value={contextValue}>
      {children}
    </FileAttachmentsContext.Provider>
  );
}

// ============================================================================
// HOOK PERSONALIZADO
// ============================================================================

export function useFileAttachments() {
  const context = useContext(FileAttachmentsContext);
  if (!context) {
    throw new Error('useFileAttachments debe usarse dentro de FileAttachmentsProvider');
  }
  return context;
}

export default FileAttachmentsContext;